import React from 'react'

/**
 * Top-level error boundary. Catches rendering/lifecycle errors anywhere in
 * the child tree and shows a friendly fallback instead of a blank white page.
 */
export default class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    // Log for debugging; real error reporting (Sentry etc.) can plug in here.
    console.error('[ErrorBoundary]', error, info)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="min-h-screen bg-[#faf7f0] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="w-14 h-14 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-7 h-7 text-rose-500">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-[#1e2a2e] mb-2">Something went wrong</h1>
          <p className="text-sm text-slate-600 mb-5">
            The app hit an unexpected error. Reload the page to recover.
            If this keeps happening, let Oliver know.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#cfa190] text-white rounded-lg hover:bg-[#c09180] transition-colors"
          >
            Reload page
          </button>
          {import.meta.env.DEV && this.state.error && (
            <details className="mt-6 text-left text-xs text-slate-500 bg-slate-50 rounded p-3">
              <summary className="cursor-pointer font-medium">Error details (dev only)</summary>
              <pre className="mt-2 whitespace-pre-wrap overflow-x-auto">{this.state.error.message}</pre>
            </details>
          )}
        </div>
      </div>
    )
  }
}
